import React, { useRef } from 'react';
import {
  PlusIcon,
  TypeIcon,
  ImageIcon,
  TrashIcon,
  GripVerticalIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from 'lucide-react';
import { VariablePicker } from './VariablePicker';
import { ImageBlockEditor } from './ImageBlockEditor';
import type { TemplateBlock, TextBlock, ImageBlock } from '../../../lib/types/template.types';

interface ContentBlockEditorProps {
  blocks: TemplateBlock[];
  onChange: (blocks: TemplateBlock[]) => void;
  error?: string;
}

export function ContentBlockEditor({ blocks, onChange, error }: ContentBlockEditorProps) {
  const handleAddTextBlock = () => {
    const newBlock: TextBlock = {
      type: 'text',
      content: '',
    };
    onChange([...blocks, newBlock]);
  };

  const handleAddImageBlock = () => {
    const newBlock: ImageBlock = {
      type: 'image',
      storage_url: '',
      caption: '',
    };
    onChange([...blocks, newBlock]);
  };

  const handleUpdateBlock = (index: number, updatedBlock: TemplateBlock) => {
    const newBlocks = [...blocks];
    newBlocks[index] = updatedBlock;
    onChange(newBlocks);
  };

  const handleDeleteBlock = (index: number) => {
    onChange(blocks.filter((_, i) => i !== index));
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newBlocks = [...blocks];
    [newBlocks[index - 1], newBlocks[index]] = [newBlocks[index], newBlocks[index - 1]];
    onChange(newBlocks);
  };

  const handleMoveDown = (index: number) => {
    if (index === blocks.length - 1) return;
    const newBlocks = [...blocks];
    [newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]];
    onChange(newBlocks);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Content Blocks
          <span className="text-red-500 ml-1">*</span>
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleAddTextBlock}
            className="px-3 py-1.5 bg-[#5B8E87] text-white rounded-lg hover:bg-[#4F7A74] transition-colors flex items-center gap-1 text-sm font-medium"
          >
            <TypeIcon className="w-4 h-4" />
            Add Text
          </button>
          <button
            type="button"
            onClick={handleAddImageBlock}
            className="px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-1 text-sm font-medium"
          >
            <ImageIcon className="w-4 h-4" />
            Add Image
          </button>
        </div>
      </div>

      {blocks.length === 0 ? (
        <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
              <PlusIcon className="w-6 h-6 text-gray-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-1">
                No content blocks yet
              </h3>
              <p className="text-sm text-gray-500">
                Add text or image blocks to build your template
              </p>
            </div>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={handleAddTextBlock}
                className="px-4 py-2 bg-[#5B8E87] text-white rounded-lg hover:bg-[#4F7A74] transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <TypeIcon className="w-4 h-4" />
                Add Text Block
              </button>
              <button
                type="button"
                onClick={handleAddImageBlock}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <ImageIcon className="w-4 h-4" />
                Add Image Block
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {blocks.map((block, index) => (
            <div
              key={index}
              className="bg-gray-50 rounded-xl p-4 border border-gray-200"
            >
              {/* Block Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <GripVerticalIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">
                    {block.type === 'text' ? 'Text Block' : 'Image Block'} #{index + 1}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {/* Move Up */}
                  <button
                    type="button"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    className={`p-1 rounded hover:bg-gray-200 transition-colors ${
                      index === 0 ? 'opacity-30 cursor-not-allowed' : ''
                    }`}
                  >
                    <ChevronUpIcon className="w-4 h-4 text-gray-600" />
                  </button>
                  {/* Move Down */}
                  <button
                    type="button"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === blocks.length - 1}
                    className={`p-1 rounded hover:bg-gray-200 transition-colors ${
                      index === blocks.length - 1 ? 'opacity-30 cursor-not-allowed' : ''
                    }`}
                  >
                    <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                  </button>
                  {/* Delete */}
                  <button
                    type="button"
                    onClick={() => handleDeleteBlock(index)}
                    className="p-1 rounded hover:bg-red-100 transition-colors"
                  >
                    <TrashIcon className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>

              {/* Block Content */}
              {block.type === 'text' ? (
                <TextBlockEditor
                  block={block}
                  onChange={(updated) => handleUpdateBlock(index, updated)}
                />
              ) : block.type === 'image' ? (
                <ImageBlockEditor
                  block={block}
                  onChange={(updated) => handleUpdateBlock(index, updated)}
                />
              ) : null}
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
}

interface TextBlockEditorProps {
  block: TextBlock;
  onChange: (block: TextBlock) => void;
}

function TextBlockEditor({ block, onChange }: TextBlockEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleInsertVariable = (variableName: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = block.content;
    const variable = `{{${variableName}}}`;

    const newContent = text.substring(0, start) + variable + text.substring(end);
    onChange({ ...block, content: newContent });

    // Set cursor position after variable
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + variable.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">
          Text Content
        </label>
        <VariablePicker onInsert={handleInsertVariable} />
      </div>
      <textarea
        ref={textareaRef}
        value={block.content}
        onChange={(e) => onChange({ ...block, content: e.target.value })}
        placeholder="Type your message here... Use {{variable_name}} for dynamic content"
        rows={4}
        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all resize-y"
      />
      <p className="text-xs text-gray-500">
        Variables will be replaced with actual values when sending the message
      </p>
    </div>
  );
}
